import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Loader2, X, Briefcase, BarChart3, Mail } from "lucide-react";
import { api } from "../lib/api";
import { logError } from "../lib/log";

const TYPE_META = {
  ic:    { icon: Briefcase, label: "IC",    color: "text-pink-700",    bg: "bg-pink-50/70 border-pink-200" },
  mis:   { icon: BarChart3, label: "MIS",   color: "text-emerald-700", bg: "bg-emerald-50/70 border-emerald-200" },
  inbox: { icon: Mail,      label: "Inbox", color: "text-amber-700",   bg: "bg-amber-50/70 border-amber-200" },
};

export default function TopbarSearch() {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const wrapRef = useRef(null);
  const navigate = useNavigate();

  // Cmd/Ctrl+K to focus
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      } else if (e.key === "Escape") {
        setOpen(false);
        inputRef.current?.blur();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Close popover on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // Debounced search
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setResults([]); setLoading(false); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q: term, limit: 8 } });
        setResults(data.results || []);
        setActiveIdx(0);
      } catch (e) {
        logError("search failed", e);
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 220);
    return () => clearTimeout(t);
  }, [q]);

  const choose = useCallback((r) => {
    setOpen(false);
    setQ("");
    setResults([]);
    if (r?.href) navigate(r.href);
  }, [navigate]);

  const onInputKey = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, Math.max(results.length - 1, 0)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const r = results[activeIdx];
      if (r) choose(r);
    }
  };

  const showPopover = open && q.trim().length >= 2;

  return (
    <div ref={wrapRef} className="relative" data-testid="topbar-search-wrap">
      <div className="hidden md:flex items-center gap-2 px-3 py-2 rounded-full border border-brand-line bg-brand-paper w-72">
        <Search className="w-4 h-4 text-zinc-400 flex-shrink-0" />
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onInputKey}
          placeholder="Search a stop, partner, lead…"
          data-testid="topbar-search"
          className="bg-transparent text-sm w-full outline-none placeholder:text-zinc-400"
        />
        {q ? (
          <button
            onClick={() => { setQ(""); setResults([]); inputRef.current?.focus(); }}
            data-testid="topbar-search-clear"
            className="text-zinc-400 hover:text-zinc-700"
            aria-label="Clear search"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        ) : (
          <span className="font-mono-tech text-[10px] text-zinc-400 border border-brand-line rounded px-1.5 py-0.5">⌘K</span>
        )}
      </div>

      {showPopover && (
        <div
          className="absolute right-0 top-full mt-2 w-[420px] bg-white border border-brand-line rounded-xl shadow-2xl overflow-hidden z-50"
          data-testid="topbar-search-popover"
        >
          {loading && (
            <div className="px-4 py-3 text-[12px] text-zinc-500 flex items-center gap-2" data-testid="topbar-search-loading">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Searching…
            </div>
          )}
          {!loading && results.length === 0 && (
            <div className="px-4 py-6 text-center text-[12px] text-zinc-400" data-testid="topbar-search-empty">
              No matches for <b className="text-zinc-600">"{q}"</b>
            </div>
          )}
          {!loading && results.length > 0 && (
            <ul className="max-h-[60vh] overflow-y-auto" data-testid="topbar-search-results">
              {results.map((r, i) => {
                const meta = TYPE_META[r.type] || TYPE_META.ic;
                const Icon = meta.icon;
                const active = i === activeIdx;
                return (
                  <li
                    key={`${r.type}-${r.id}`}
                    onMouseEnter={() => setActiveIdx(i)}
                    onClick={() => choose(r)}
                    data-testid={`topbar-search-result-${i}`}
                    className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer border-b border-zinc-100 last:border-0 ${active ? "bg-brand-paper" : "hover:bg-zinc-50"}`}
                  >
                    <span className={`flex-shrink-0 w-7 h-7 rounded-md border flex items-center justify-center ${meta.bg}`}>
                      <Icon className={`w-3.5 h-3.5 ${meta.color}`} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="text-[13px] font-bold text-zinc-900 truncate" title={r.title}>{r.title}</div>
                      <div className="text-[11px] text-zinc-500 truncate" title={r.subtitle}>{r.subtitle}</div>
                    </div>
                    <span className={`flex-shrink-0 text-[9.5px] uppercase tracking-wider font-bold ${meta.color}`}>
                      {meta.label}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
          <div className="px-3 py-1.5 border-t border-zinc-100 text-[10px] text-zinc-400 flex items-center justify-between bg-brand-paper">
            <span>↑↓ navigate · Enter open · Esc close</span>
            <span>{results.length} match{results.length === 1 ? "" : "es"}</span>
          </div>
        </div>
      )}
    </div>
  );
}
